import React, { useEffect } from "react"
import FeedbackButton from "../components/FeedbackButton"
import "./LandingPage.css"

const heroSignals = [
  "Private by design",
  "Multi-agent collaboration",
  "Governed tools and approvals",
  "Operational visibility built in",
]

const valuePoints = [
  {
    title: "Private AI agent platform",
    text: "Self-host on your own infrastructure. Your agents, your data, your network — nothing leaves your control.",
  },
  {
    title: "Governed automation",
    text: "Approval flows, credential vaults, audit logs, and risk classification so teams can automate without chaos.",
  },
  {
    title: "Knowledge that compounds",
    text: "Turn documents, trusted links, and internal notes into reusable local expertise for your agents.",
  },
]

const workflowSteps = [
  {
    step: "01",
    title: "Create agents",
    text: "Define an agent’s purpose, capabilities, and expertise with a guided creation flow.",
  },
  {
    step: "02",
    title: "Teach with local knowledge",
    text: "Build skill packs from uploads, pasted notes, and trusted web documentation.",
  },
  {
    step: "03",
    title: "Connect real channels",
    text: "Run agents in Discord, Slack, Telegram, and WhatsApp where teams already communicate.",
  },
  {
    step: "04",
    title: "Operate with confidence",
    text: "Use one control plane for approvals, health, activity, integrations, and credentials.",
  },
]

const useCases = [
  {
    title: "IT operations and troubleshooting",
    text: "Support incident handling, troubleshooting, service coordination, and practical daily operations work.",
  },
  {
    title: "Multi-agent collaboration",
    text: "Let specialized agents research, execute, document, and hand work to each other instead of acting alone.",
  },
  {
    title: "Knowledge-driven automation",
    text: "Build agents that learn from your organization’s documentation instead of depending only on generic model behavior.",
  },
]

const faqItems = [
  {
    q: "Do I need to be highly technical to use AgentColab?",
    a: "No. The installer and user guide walk you through setup step by step. If you can run a terminal command, you can get running.",
  },
  {
    q: "Is my data private?",
    a: "Yes. AgentColab is fully self-hosted on your infrastructure. Your agents, conversations, credentials, and knowledge never leave your network.",
  },
  {
    q: "Can agents work in messaging channels?",
    a: "Yes. AgentColab supports operational collaboration through Discord, Slack, Telegram, and WhatsApp.",
  },
  {
    q: "Can I teach agents using my own documentation?",
    a: "Yes. You can ingest documents, pasted notes, and trusted web links, then save that knowledge as reusable skill packs.",
  },
]

export default function LandingPage() {
  useEffect(() => {
    document.title = "AgentColab | Self-Hosted AI Agent Platform for Teams"

    const upsertMeta = (name, content, attr = "name") => {
      let tag = document.head.querySelector(`meta[${attr}="${name}"]`)
      if (!tag) {
        tag = document.createElement("meta")
        tag.setAttribute(attr, name)
        document.head.appendChild(tag)
      }
      tag.setAttribute("content", content)
    }

    const upsertLink = (rel, href) => {
      let tag = document.head.querySelector(`link[rel="${rel}"]`)
      if (!tag) {
        tag = document.createElement("link")
        tag.setAttribute("rel", rel)
        document.head.appendChild(tag)
      }
      tag.setAttribute("href", href)
    }

    upsertMeta(
      "description",
      "AgentColab is a self-hosted AI agent platform for teams. Create governed agents, teach them your knowledge, connect to Slack or Telegram, with approvals, credentials, and audit logs."
    )
    upsertMeta("og:title", "AgentColab | Self-Hosted AI Agent Platform for Teams", "property")
    upsertMeta(
      "og:description",
      "Your team's AI agents — governed, private, and ready for real work. Self-hosted with approvals, credentials, audit logs, and channel integrations.",
      "property"
    )
    upsertMeta("og:type", "website", "property")
    upsertMeta("og:image", `${window.location.origin}/branding/agentcolab-mark.svg`, "property")
    upsertMeta("twitter:card", "summary_large_image")
    upsertMeta("twitter:title", "AgentColab | Self-Hosted AI Agent Platform for Teams")
    upsertMeta(
      "twitter:description",
      "Your team's AI agents — governed, private, and ready for real work. Self-hosted with approvals, credentials, and channel integrations."
    )
    upsertLink("canonical", "https://agentcolab.io/")
  }, [])

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "AgentColab",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Linux, macOS, Windows",
    description:
      "AgentColab is a self-hosted AI agent platform for teams. Create governed agents, teach them your knowledge, connect to Slack or Telegram, with approvals, credentials, and audit logs.",
    publisher: {
      "@type": "Organization",
      name: "AgentColab",
      url: "https://agentcolab.io/",
      logo: "https://agentcolab.io/branding/agentcolab-mark.svg",
    },
  }

  return (
    <div className="lp2-shell">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />

      <header className="lp2-header">
        <nav className="lp2-nav">
          <a className="lp2-brand" href="/landing">
            <img src="/branding/agentcolab-logo.svg" alt="AgentColab" />
          </a>
          <div className="lp2-nav-links">
            <a href="#quick-start">Quick Start</a>
            <a href="/user-guide">User Guide</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#faq">FAQ</a>
            <a className="lp2-button lp2-button--ghost" href="/installers">Download</a>
          </div>
        </nav>

        <section className="lp2-hero">
          <div className="lp2-hero-copy">
            <div className="lp2-eyebrow">Self-hosted AI agent platform for teams</div>
            <h1>Your team's AI agents — governed, private, and ready for real work.</h1>
            <p className="lp2-tagline">
              Create specialized agents, teach them your organization's knowledge, connect them to
              Slack or Telegram, and control everything with approvals, credentials, and audit logs
              — all self-hosted on your infrastructure.
            </p>
            <div className="lp2-actions">
              <a className="lp2-button lp2-button--primary" href="#quick-start">Get Started</a>
              <a className="lp2-button lp2-button--secondary" href="/user-guide">See How It Works</a>
            </div>
            <div className="lp2-chip-block">
              <div className="lp2-chip-label">Trusted by teams that need governance, not just AI</div>
              <div className="lp2-chip-row">
              {heroSignals.map((item) => (
                <span key={item} className="lp2-chip">{item}</span>
              ))}
              </div>
            </div>
          </div>

          <div className="lp2-hero-art" aria-label="Illustration of agents collaborating">
            <div className="lp2-art-stage">
              <div className="lp2-art-network" role="img" aria-label="Hub-and-spoke orchestrator network">
                <div className="lp2-art-node lp2-art-node--hub">
                  <img src="/branding/agentcolab-mark.svg" alt="AgentColab mark" />
                  <span>Orchestrator</span>
                </div>

                <div className="lp2-art-spoke lp2-art-spoke--top" aria-hidden="true" />
                <div className="lp2-art-spoke lp2-art-spoke--left" aria-hidden="true" />
                <div className="lp2-art-spoke lp2-art-spoke--right" aria-hidden="true" />
                <div className="lp2-art-spoke lp2-art-spoke--bottom" aria-hidden="true" />

                <div className="lp2-art-node lp2-art-node--top">
                  <strong>Research</strong>
                  <p>Finds context and options.</p>
                </div>
                <div className="lp2-art-node lp2-art-node--left">
                  <strong>Operations</strong>
                  <p>Executes with approvals.</p>
                </div>
                <div className="lp2-art-node lp2-art-node--right">
                  <strong>Knowledge</strong>
                  <p>Builds reusable expertise.</p>
                </div>
                <div className="lp2-art-node lp2-art-node--bottom">
                  <strong>Delivery</strong>
                  <p>Routes work into channels.</p>
                </div>
              </div>
            </div>

            <div className="lp2-art-caption">
              <div className="lp2-caption-item">
                <strong>Control Plane</strong>
                <span>One place for agents, credentials, integrations, and approvals.</span>
              </div>
              <div className="lp2-caption-item">
                <strong>Knowledge Packs</strong>
                <span>Grow local expertise from docs, uploads, and trusted links.</span>
              </div>
              <div className="lp2-caption-item">
                <strong>Channel Delivery</strong>
                <span>Route work into Discord, Telegram, Slack, and WhatsApp.</span>
              </div>
            </div>
          </div>
        </section>
      </header>

      <main className="lp2-main">
        <section className="lp2-stats">
          <div className="lp2-stat"><strong>4</strong><span>Live channel integrations</span></div>
          <div className="lp2-stat"><strong>12+</strong><span>Governed capabilities</span></div>
          <div className="lp2-stat"><strong>100%</strong><span>Self-hosted and private</span></div>
          <div className="lp2-stat"><strong>3</strong><span>Platform installers (Linux, macOS, Windows)</span></div>
        </section>

        <section className="lp2-section lp2-values">
          {valuePoints.map((item) => (
            <article key={item.title} className="lp2-value">
              <div className="lp2-mini-kicker">Core advantage</div>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </section>

        <section id="quick-start" className="lp2-section">
          <div className="lp2-section-head">
            <div className="lp2-eyebrow">Quick start</div>
            <h2>From download to running agents in under 10 minutes.</h2>
            <p>
              One installer command, one setup script, and you have a running multi-agent platform
              with a dashboard, knowledge tools, and channel integrations.
            </p>
          </div>

          <div className="lp2-quickstart">
            <div className="lp2-flow-panel">
              <div className="lp2-panel-title">What happens next</div>
              <ol>
                <li>Download the installer for your platform.</li>
                <li>Download the installer for Linux, macOS, or Windows.</li>
                <li>Run the installer — it handles prerequisites, setup, and configuration.</li>
                <li>Open the dashboard and create your first agent.</li>
              </ol>
            </div>
          </div>
        </section>

        <section className="lp2-section">
          <div className="lp2-section-head">
            <div className="lp2-eyebrow">Where it fits</div>
            <h2>A modern AI operations platform for real teams.</h2>
          </div>
          <div className="lp2-grid lp2-grid--three">
            {useCases.map((card) => (
              <article key={card.title} className="lp2-card">
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="how-it-works" className="lp2-section">
          <div className="lp2-section-head">
            <div className="lp2-eyebrow">How it works</div>
            <h2>From creation to live operations in four clear steps.</h2>
          </div>
          <div className="lp2-workflow">
            {workflowSteps.map((item) => (
              <article key={item.step} className="lp2-step">
                <div className="lp2-step-badge">{item.step}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="lp2-section lp2-overview">
          <div className="lp2-overview-copy">
            <div className="lp2-eyebrow">Platform overview</div>
            <h2>One control plane for AI agents, knowledge, tools, and operations.</h2>
            <p>
              AgentColab brings prompts, skills, credentials, approvals, and channel routing into a single
              governed workspace instead of scattering them across different tools.
            </p>
            <ul>
              <li>Wizard-based agent creation</li>
              <li>Skill packs and local documentation ingestion</li>
              <li>Credential controls and approvals</li>
              <li>Messaging integrations for real operational delivery</li>
            </ul>
          </div>

          <div className="lp2-overview-card">
            <div className="lp2-overview-top">
              <img src="/branding/agentcolab-mark.svg" alt="AgentColab product mark" />
              <div>
                <strong>Product snapshot</strong>
                <span>Clean operator experience for governed AI deployment</span>
              </div>
            </div>
            <div className="lp2-overview-grid">
              <div className="lp2-overview-item">
                <strong>Agent creation</strong>
                <span>Function, tools, skills, and onboarding in one guided flow.</span>
              </div>
              <div className="lp2-overview-item">
                <strong>Knowledge ingestion</strong>
                <span>Paste notes, upload docs, add links, merge duplicates, and save packs.</span>
              </div>
              <div className="lp2-overview-item">
                <strong>Operational delivery</strong>
                <span>Move from dashboard setup into real channels without losing control.</span>
              </div>
              <div className="lp2-overview-item">
                <strong>Safe rollout</strong>
                <span>Use versioned installers, repair flows, and upgrade support for production use.</span>
              </div>
            </div>
          </div>
        </section>

        <section id="faq" className="lp2-section">
          <div className="lp2-section-head">
            <div className="lp2-eyebrow">FAQ</div>
            <h2>Questions people ask before they commit time.</h2>
          </div>
          <div className="lp2-grid lp2-grid--two">
            {faqItems.map((item) => (
              <article key={item.q} className="lp2-card lp2-card--faq">
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="lp2-section">
          <div className="lp2-final-cta">
            <div>
              <div className="lp2-eyebrow">Get started</div>
              <h2>Ready to put AI agents to work for your team?</h2>
              <p>
                Download the installer for your platform and have your first agent running
                in minutes. No account required.
              </p>
            </div>
            <div className="lp2-actions">
              <a className="lp2-button lp2-button--primary" href="/installers">
                Download Installers
              </a>
              <a className="lp2-button lp2-button--secondary" href="/user-guide">
                Read the User Guide
              </a>
            </div>
          </div>
        </section>
      </main>
      <FeedbackButton />
    </div>
  )
}
